import { getClient } from '@/api/api-client';
import type { ICockpitAxiosRequestConfig } from '@/api/api-client';
import { type IdTokenResult, SAMLAuthProvider, type User, getAuth, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import Cookies from 'js-cookie';

/** Decodes the payload of a JWT without verification (payload only). */
function decodeJwtPayload(token: string): Record<string, unknown> {
    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
    }
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = atob(payloadB64);
    return JSON.parse(payloadJson) as Record<string, unknown>;
}

export const authManager = {
    token: '',

    async logIn() {
        // @ts-expect-error
        const provider = new SAMLAuthProvider('saml.onelogin');
        return signInWithPopup(getAuth(), provider);
    },

    async logOut() {
        const sessionCookie = Cookies.get('reneo_session');
        if (sessionCookie) {
            Cookies.remove('reneo_session', { path: '/', domain: '.condogroup.tech' });
            Cookies.remove('reneo_session', { path: '/' });
            return;
        }

        return getAuth().signOut();
    },

    async logInWithEmailAndPassword(email: string, password: string) {
        return signInWithEmailAndPassword(getAuth(), email, password);
    },

    async logInWithEmailAndPasswordViaCookie(email: string, password: string) {
        // POST /authz/session-cookie – cookie is set via Set-Cookie header, response body contains the JWT
        const { data } = await getClient('basecamp').post<{ sessionCookie: string }>('/authz/session-cookie', { username: email, password }, {
            skipAuth: true,
            withCredentials: true,
        } as ICockpitAxiosRequestConfig);

        return decodeJwtPayload(data.sessionCookie);
    },

    async getToken() {
        if (!getAuth().currentUser) {
            return Promise.reject('User Not Authenticated');
        }

        this.token = await getAuth().currentUser.getIdToken();

        return this.token;
    },

    async getRefreshToken() {
        if (!getAuth().currentUser) {
            return Promise.reject('User Not Authenticated');
        }

        // Access internal refreshToken from stsTokenManager
        // Note: This is an internal Firebase property and may change in future versions
        const user = getAuth().currentUser as any;
        return user.stsTokenManager?.refreshToken;
    },

    getUserDataForPasswordProvider(idResult: IdTokenResult) {
        return {
            userId: idResult.claims.user_id,
            firstname: 'Test',
            lastname: 'User',
            email: idResult.claims.email,
            activeRole: 'test-user',
        };
    },

    getUserDataForSAMLProvider(v: IdTokenResult) {
        //Firebase return everything is some strange format somehow and umlauts not parsed
        // Most probably it is due to encoding on firebase side which doesn't have configurations
        // this piece of code should be fault-tolerant or whole page will not load
        let firstname = v.claims.firebase['User.FirstName'] ?? v.claims.firebase['sign_in_attributes']?.['User.FirstName'];
        let lastname = v.claims.firebase['User.LastName'] ?? v.claims.firebase['sign_in_attributes']?.['User.LastName'];
        try {
            firstname = decodeURIComponent(escape(firstname));
            lastname = decodeURIComponent(escape(lastname));
        } catch (e) {
            /** do nothing */
        }
        return {
            userId: v.claims.user_id,
            firstname,
            lastname,
            email: v.claims.firebase['User.email'] ?? v.claims.firebase['sign_in_attributes']?.['User.email'],
            activeRole: v.claims.activeRole,
        };
    },

    async fetchUserData(user: User): Promise<object> {
        if (user) {
            return user.getIdTokenResult().then(v => {
                if (v.claims.firebase.sign_in_provider === 'password') {
                    return this.getUserDataForPasswordProvider(v);
                }
                return this.getUserDataForSAMLProvider(v);
            });
        }
        return null;
    },

    fetchSessionCookie() {
        return Cookies.get('reneo_session');
    },

    /*
        Identity provider data available in result.additionalUserInfo.profile,
        or from the user's ID token obtained via result.user.getIdToken()
        as an object in the firebase.sign_in_attributes custom claim
        This is also available via result.user.getIdTokenResult()
        idTokenResult.claims.firebase.sign_in_attributes.
     */
    async getUserData() {
        const sessionCookie = Cookies.get('reneo_session');

        if (sessionCookie) {
            try {
                return decodeJwtPayload(sessionCookie);
            } catch {
                // Cookie present but invalid (e.g. expired), fall through to Firebase
            }
        }

        // Firebase don't actually have this API, so we use "subscribe once". Callback will be triggered anyway, it is just user might be null
        return new Promise((resolve, reject) => {
            const unsubscribe = getAuth().onAuthStateChanged(user => {
                unsubscribe();
                if (user) {
                    authManager.fetchUserData(user).then(data => resolve(data));
                } else {
                    resolve(user);
                }
            }, reject);
        });
    },
};
